import { useState, useEffect, useCallback } from 'react';

interface VersionInfo {
  version: string;
  buildTime: string;
  gitCommit?: string;
}

export const useVersionCheck = (checkIntervalMs: number = 5 * 60 * 1000) => {
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);

  const checkVersion = useCallback(async () => {
    try {
      // Add cache-busting query param to ensure fresh data
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
        }
      });
      
      if (!response.ok) {
        console.warn('Failed to fetch version info');
        return;
      }

      const versionInfo: VersionInfo = await response.json();
      
      // First time - store current version
      if (!currentVersion) {
        setCurrentVersion(versionInfo.version);
        setLatestVersion(versionInfo.version);
        return;
      }

      // Check if version changed
      if (versionInfo.version !== currentVersion) {
        console.log('🎉 New version available!', {
          current: currentVersion,
          latest: versionInfo.version
        });
        setLatestVersion(versionInfo.version);
        setNewVersionAvailable(true);
      }
    } catch (error) {
      console.warn('Version check failed:', error);
    }
  }, [currentVersion]);

  const refreshApp = useCallback(() => {
    // Clear caches and reload
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    window.location.reload();
  }, []);

  const dismissUpdate = useCallback(() => {
    setNewVersionAvailable(false);
    // Update current version to latest so we don't show again until next deployment
    setCurrentVersion(latestVersion);
  }, [latestVersion]);

  useEffect(() => {
    // Check version on mount
    checkVersion();

    // Set up interval to check periodically
    const interval = setInterval(checkVersion, checkIntervalMs);

    // Check when window regains focus (user comes back to tab)
    const handleFocus = () => checkVersion();
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [checkVersion, checkIntervalMs]);

  return {
    newVersionAvailable,
    currentVersion,
    latestVersion,
    refreshApp,
    dismissUpdate,
    checkVersion
  };
};

